import { useEffect, useState } from "react";
import Modal from "@modalSrc/Modal";
import RightTimeApi from "@/API";
import Helpers from "@/Helpers";

type AssignMode = "existingUser" | "newUser";

type AssignableTest = {
  id: number;
  companyTestTypeName?: string;
  firstName?: string;
  lastName?: string;
  finished?: string;
  [key: string]: unknown;
};

type AssignUserTestsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  companyId?: number | string;
  userId?: number | string;
  caption?: string;
  buttonText?: string;
  mode?: AssignMode;
};

export default function AssignUserTestsModal({
  isOpen,
  onClose,
  companyId,
  userId,
  caption,
  buttonText,
  mode = "existingUser"
}: AssignUserTestsModalProps) {
  const [tests, setTests] = useState<AssignableTest[]>([]);
  const [selectedTests, setSelectedTests] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState("lastName");
  const [sortDirection, setSortDirection] = useState("asc");

  useEffect(() => {
    if (!isOpen) return;
    setSelectedTests([]);
    getTestsAsync();
  }, [isOpen, companyId]);

  async function getTestsAsync() {
    if (!companyId) return;
    setLoading(true);
    const resp = await RightTimeApi.getAsync(`/Tests/${companyId}/Unassigned`);
    if (resp && resp.ok) {
      const list = (resp.jsonData?.tests || []) as AssignableTest[];
      list.sort((a, b) => {
        const aLast = (a.lastName || "").toLowerCase();
        const bLast = (b.lastName || "").toLowerCase();
        const aFirst = (a.firstName || "").toLowerCase();
        const bFirst = (b.firstName || "").toLowerCase();
        if (aLast < bLast) return -1;
        if (aLast > bLast) return 1;
        if (aFirst < bFirst) return -1;
        if (aFirst > bFirst) return 1;
        return 0;
      });
      setTests(list);
    }
    setLoading(false);
  }

  function handleSort(column: string) {
    let nextDirection = sortDirection;
    if (sortColumn === column) {
      nextDirection = sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(nextDirection);
    } else {
      setSortColumn(column);
      nextDirection = "asc";
      setSortDirection("asc");
    }

    const sorted = [...tests].sort((a, b) => {
      const aValue = String(a[column] ?? "").toLowerCase();
      const bValue = String(b[column] ?? "").toLowerCase();

      if (aValue < bValue) return nextDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return nextDirection === "asc" ? 1 : -1;
      return 0;
    });

    setTests(sorted);
  }

  function toggleRowSelection(e: React.MouseEvent<HTMLTableRowElement>) {
    const testId = parseInt(e.currentTarget.getAttribute("data-id"), 10);
    const selected = selectedTests.includes(testId);
    if (selected) {
      setSelectedTests(selectedTests.filter((id) => id !== testId));
    } else {
      setSelectedTests([...selectedTests, testId]);
    }
  }

  async function associateTestsAsync() {
    switch (mode) {
      case "existingUser":
        if (companyId && userId && selectedTests.length > 0) {
          const apiData = { companyId: companyId, userId: userId, testIds: selectedTests };
          setTests([]);
          setSelectedTests([]);
          const resp = await RightTimeApi.postAsync(`/Tests/Assign`, apiData);
          if (resp && resp.ok) {
            onClose();
          }
        }
        break;
      case "newUser":
        await Promise.all(selectedTests.map((testId) => RightTimeApi.postAsync(`/CreateUserFromTest/${testId}`)));
        setSelectedTests([]);
        onClose();
        break;
      default:
        break;
    }
  }

  if (!isOpen || loading) return null;

  return (
    <Modal
      isOpen={isOpen}
      title={caption || "Assign Tests"}
      onClose={onClose}
      className="assignUserTestsModal"
    >
      <div className="tableWrapper factTable scrollable">
        <table className="formFields">
          <caption>
            <span className="title">{caption || "Assign Tests"}</span>
          </caption>
          <thead>
            <tr>
              <th></th>
              <th onClick={() => handleSort("companyTestTypeName")}>
                Type {sortColumn === "companyTestTypeName" && (sortDirection === "asc" ? "▲" : "▼")}
              </th>
              <th onClick={() => handleSort("firstName")}>
                First Name {sortColumn === "firstName" && (sortDirection === "asc" ? "▲" : "▼")}
              </th>
              <th onClick={() => handleSort("lastName")}>
                Last Name {sortColumn === "lastName" && (sortDirection === "asc" ? "▲" : "▼")}
              </th>
              <th onClick={() => handleSort("finished")}>
                Date {sortColumn === "finished" && (sortDirection === "asc" ? "▲" : "▼")}
              </th>
            </tr>
          </thead>
          <tbody>
            {tests && tests.length > 0 ? tests.map((test) => (
              <tr
                onClick={toggleRowSelection}
                key={test.id}
                data-id={test.id}
                className={selectedTests.includes(test.id) ? "selected" : ""}
              >
                <td>{test.id}</td>
                <td>{test.companyTestTypeName}</td>
                <td>{test.firstName}</td>
                <td>{test.lastName}</td>
                <td>{Helpers.EST(test.finished, "MM/dd/yyyy")}</td>
              </tr>
            )) : null}
          </tbody>
        </table>
      </div>
      <div className="actions">
        <button className="btn btn-primary" onClick={associateTestsAsync}>{buttonText || "Assign Tests"}</button>
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
}


